import store from '@/store'
import Status from 'App/Http/Status'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { attachAbortSignal } from 'App/Http/pendingRequests'
import { getTokenExpiry } from 'App/Helpers'

/*
|--------------------------------------------------------------------------
| Axios interceptors
|--------------------------------------------------------------------------
|
| Global Axios interceptors run before a request is sent and can transform
| a response once it is received.
|
*/

/**
 * The API endpoints (w/out base url - api/v1) that should not have
 * an Authorization header & bearer token attached to the request.
 *
 * @type {Array}
 */
const excluded = ['v1/login', 'v1/login/options', 'v1/sso/token']
const loginUrls = ['v1/login', 'v1/sso/token']

/**
 * The auth API endpoints. A 401 from these means the request itself was
 * rejected, not that the session's access token has expired.
 *
 * @type {Array}
 */
const authEndpoints = ['v1/login', 'v1/logout', 'v1/token']

/**
 * Refresh the access token before a request when it expires within this many
 * milliseconds, e.g. after the computer wakes from sleep.
 *
 * @type {Number}
 */
const REFRESH_BEFORE_REQUEST_MS = 60 * 1000

/**
 * True when a 401 is a failed auth request, not an expired session.
 *
 * @param {String} url
 * @return {Boolean}
 */
function isAuthRequestUrl(url) {
	return excluded.includes(url) || authEndpoints.includes(url)
}

/**
 * Axios global request interceptors.
 *
 * @type {Array}
 */
const request = [
	attachAbortSignal,
	/**
	 * Ensure session has not exceeded 15 days, then attach the bearer token,
	 * refreshing it first if it is about to expire.
	 * If session is expired, the API call is not sent and user is redirected to login.
	 *
	 * @param {Object} config
	 * @return {Object|Promise}
	 */
	async config => {
		const url = config.url

		if (excluded.includes(url)) {
			return config
		}

		const sessionValid = await store.dispatch('auth/checkSessionExpiry')
		if (!sessionValid) {
			return Promise.reject(new Error('Session expired'))
		}

		const { session } = store.state.auth
		if (
			session.access_token &&
			!authEndpoints.includes(url) &&
			getTokenExpiry(session) - Date.now() < REFRESH_BEFORE_REQUEST_MS
		) {
			await store.dispatch('auth/refreshAuthenticationInfo')
		}

		const { access_token } = store.state.auth.session
		if (access_token) {
			config.headers.Authorization = `Bearer ${access_token}`
		}

		return config
	},
]

/**
 * Axios global response interceptors.
 *
 * @type {Array}
 */
const response = [
	{
		/**
		 * Success response interceptor.
		 *
		 * @param {Object} response
		 * @return {Object}
		 */
		success: response => {
			const userRoles = get(response, 'data.data.roles', [])
			const url = get(response, 'config.url', '')

			// Restrict some user role to login into OMD,
			// until we get permission based login support in API
			if (loginUrls.includes(url)) {
				const restrictedRole = [
					'transcriptionist',
					'transcriptionist-manager',
					'transcriptionist-qa',
				]
				const allowedRoles = []
				userRoles.forEach(({ name }) => {
					if (!restrictedRole.includes(name)) {
						allowedRoles.push(name)
					}
				})
				if (!isEmpty(userRoles) && isEmpty(allowedRoles)) {
					return {
						status: 401,
					}
				}
			}
			return response
		},

		/**
		 * Error response interceptor. On a 401, refresh the access token once
		 * and retry the request before logging the user out.
		 *
		 * @param {Error} error
		 * @param {Axios} http
		 * @return {Promise}
		 */
		error: async (error, http) => {
			const config = error?.config || {}

			if (
				error?.response?.status === Status.UNAUTHENTICATED &&
				!isAuthRequestUrl(config.url)
			) {
				if (
					!config.retriedAfterRefresh &&
					(await store.dispatch('auth/refreshAuthenticationInfo'))
				) {
					config.retriedAfterRefresh = true

					return http(config)
				}

				await store.dispatch('auth/endSession', '401')
			}

			return Promise.reject(error)
		},
	},
]

export default {
	request,
	response,
}
